import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import PropTypes from 'prop-types';
import Input from 'src/components/form/floating-label/input';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import API from 'src/pages/complaint/api';
import Modal from 'src/components/common/modal/modal';
import { env } from 'src/constants/env';
import { ReactComponent as Report } from 'src/assets/report.svg';
import { ComplaintEntityType } from 'src/constants/complaint-entity-type';

// styles
import css from './complaint-popup.module.scss';
import 'src/utils/popup.scss';
import { toast } from 'react-toastify';
import getValidationSchema from 'src/pages/complaint/validate';

ComplaintPopup.propTypes = {
  entityId: PropTypes.string.isRequired,
  targetTitle: PropTypes.string.isRequired
};

export default function ComplaintPopup({ entityId, targetTitle }) {
  const complaintTypes = [
    { value: 'Граматична помилка', label: 'Граматична помилка' },
    { value: 'Дані не відповідають дійсності', label: 'Дані не відповідають дійсності' },
    { value: 'Інше', label: 'Інше' }
  ];
  const [complaintDetails, setComplaintDetails] = useState({ title: '', placeholder: '' });
  const [showDescriptionInput, setShowDescriptionInput] = useState(false);
  const descriptionInputId = useId();

  const validationSchema = getValidationSchema();

  const captchaRef = useRef(null);
  const registerAndClearOnChange = function (inputName) {
    return register(inputName, {
      onChange: () => {
        if (errors[inputName]) {
          clearErrors(inputName);
        }
      }
    });
  };

  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      complaintType: '',
      description: ''
    }
  });

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    watch,
    setValue,
    formState: { errors, isSubmitSuccessful }
  } = methods;
  const watchedDescription = watch('description');

  const resetCallback = useCallback(() => {
    return isSubmitSuccessful ? reset : () => {};
  }, [reset, isSubmitSuccessful]);

  useEffect(resetCallback, [resetCallback]);

  const onFormSubmit = (data, close) => {
    API.complaint({
      entityId: entityId,
      entityType: ComplaintEntityType.KB_MATERIAL,
      description: data.description,
      complaintType: data.complaintType,
      captcha: data.captcha,
      targetTitle
    })
      .then(() => {
        handleClose(close);
        toast.success('Вашу інформацію успішно надіслано');
      })
      .catch(() => toast.error('Щось пішло не так'));
  };

  function handleFeedback() {
    setComplaintDetails({ title: 'Залишити відгук', placeholder: 'Опис відгука' });
    setShowDescriptionInput(true);
    setValue('complaintType', 'Відгук');
  }

  function handleReport() {
    setComplaintDetails({ title: 'Поскаржитися', placeholder: 'Опишіть зафіксовану Вами помилку' });
  }

  function handleClose(close) {
    reset();
    setComplaintDetails({ title: '', placeholder: '' });
    setShowDescriptionInput(false);
    close?.();
  }

  return (
    <Modal
      trigger={
        <div className={css.complaintPopupTrigger}>
          <button className={css.complaintButton} onClick={handleFeedback}>
            Залишити відгук
          </button>
          <button className={css.complaintButton} onClick={handleReport}>
            <Report />
          </button>
        </div>
      }
      onClose={() => handleClose()}
      nested
      closeOnDocumentClick={!watchedDescription}>
      {(close) => (
        <div className={css.complaintPopup}>
          <button className={css.close} onClick={() => handleClose(close)}>
            &times;
          </button>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(async (data) => {
                const token = await captchaRef.current?.executeAsync();
                onFormSubmit({ ...data, captcha: token }, close);
              })}>
              <div className={css.complaintGroup}>
                {complaintDetails.title && <h1>{complaintDetails.title}</h1>}
                {!showDescriptionInput && (
                  <div className={css.group}>
                    {complaintTypes.map((complaintType, i) => (
                      <button
                        key={i} /* NOSONAR */
                        type='button'
                        onClick={() => {
                          setValue('complaintType', complaintType.value);
                          setShowDescriptionInput(true);
                        }}>
                        {complaintType.label}
                      </button>
                    ))}
                  </div>
                )}
                {showDescriptionInput && (
                  <div className={css.group}>
                    <Input
                      id={descriptionInputId}
                      label={complaintDetails.placeholder}
                      error={errors?.description?.message}
                      multiline
                      required
                      {...registerAndClearOnChange('description')}
                    />
                    <button type='submit' className={css.submit}>
                      Надіслати
                    </button>
                  </div>
                )}
              </div>
            </form>
            <ReCAPTCHA
              ref={captchaRef}
              className={css.captcha}
              sitekey={env.REACT_APP_NZ_RECAPTCHA_SITE_KEY}
              hl='uk'
              size='invisible'
            />
          </FormProvider>
        </div>
      )}
    </Modal>
  );
}
